/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import Currency, { CurrencyType } from '../Localization/Currency';
import LocalizationComponent, {DateTimeConfiguration} from '../../Scripts/Localization/LocalizationComponent';
import BetManager from '../Playbar/BetManager';
import IPlaybar from '../Playbar/IPlaybar';
import Communicator from '../Server/Communicator';
import { IPlayState } from '../Server/Object/PlayResult';
import { IRecallSummary } from '../Server/Object/RecallSummary';
import { IBetConfig } from '../Server/SetupResult';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import ReferencedComponent from '../Utility/Component/ComponentReference/ReferencedComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import { ContextKey } from '../Utility/Component/Configurator/ContextKey';
import IPopupManager from '../Utility/Component/Popup/IPopupManager';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import GameSetup, { WagerDisplay } from './GameSetup';
import { RecallEntryData } from './RecallEntry';
import { CultureID } from '../Localization/CultureID';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * RecallManager is responsible for maintaining a history of recent plays and allowing
 * the user to open a new tab and replay a recent result.
 */
@cc._decorator.ccclass
export default class RecallManager extends StateDependencyComponent
{
    /** The state on which RecallManager should check the current configuration to see if this is a recall session. */
    @property({ type: StateReference, visible: true })
    private _checkRecallOnState: StateReference = new StateReference(StateID.CheckRecovery, true);

    /** The state on which RecallManager should update its list of play history. */
    @property({ type: [StateReference], visible: true })
    protected _updateRecallDataStates: StateReference[] = [new StateReference(StateID.Recover)];

    /** A reference to the node containing the recall list UI. */
    @property({ type: InjectableComponent, visible: true })
    protected coverNodeReference: InjectableComponent<ReferencedComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, 'Recall.Cover');

    /** @hidden */
    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent();
    /** @hidden */
    private _communicatorRef: InjectableComponent<Communicator> = new InjectableComponent();
    /** @hidden */
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();
    /** @hidden */
    private _playbarRef: InjectableComponent<IPlaybar> = new InjectableComponent();
    /** @hidden */
    private _popupManagerRef: InjectableComponent<IPopupManager> = new InjectableComponent();
    /** @hidden */
    private _localizationComponent: InjectableComponent<LocalizationComponent> = new InjectableComponent();

    /** @hidden */
    private _recalling: boolean = false;
    /** Accessor for whether or not the game is performing a recall play. */
    public get Recalling(): boolean { return this._recalling; }

    /** @hidden */
    private _recallRound: number = 18;
    private _recallPlay: number = 0; // -1;
    /** Accessor for the play ID of the play being recalled. */
    public get RecallRoundID(): number { return this._recallRound; }
    public get RecallPlayID(): number { return this._recallPlay; }
    public set RecallPlayID(recallPlay: number) { this._recallPlay = recallPlay; }

    private _recallEntries: RecallEntryData[] = [];

    /** @hidden */
    protected setContexts(): void
    {
        this._betManagerRef.SetContext(BetManager);
        this._communicatorRef.SetContext(Communicator);
        this._gameSetupRef.SetContext(GameSetup);
        this._playbarRef.SetContext(ContextKey.PLAYBAR);
        this._popupManagerRef.SetContext(ContextKey.POPUP_MANAGER);
        this._localizationComponent.SetContext(LocalizationComponent);
        this.coverNodeReference.SetContext(ContextKey.REFERENCED_COMPONENT, { InjectOnce: true });
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._checkRecallOnState, this._updateRecallDataStates);

        if (this._localizationComponent.HasValue())
        {
            this._localizationComponent.Value.OnChanged(() => this.refreshRecallUi());
        }
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (this._updateRecallDataStates.some((x) => x.Value === event.State))
        {
            this.PopulateRecallData();
            this.completeState(event.State);
        }
        else if (event.State === this._checkRecallOnState.Value)
        {
            this.checkRecall();
        }
    }

    /**
     * Called when checkRecallOnState is reached.
     * Begins recall play if recall data is present in the config.
     */
    private checkRecall(): void
    {
        if (this._gameSetupRef.Value.Recall === false && this._gameSetupRef.Value.PlatformRecallRoundID == null)
        {
            this.completeState(this._checkRecallOnState.Value);
            return;
        }

        this._recalling = true;
        this._recallPlay = 0;
        this._recallRound = this._gameSetupRef.Value.PlayID;
        this.StartRecallMode();
        this._communicatorRef.Value.RequestPreviousResult(
            this.RecallRoundID,
            this.RecallPlayID,
            this.onPlayResult.bind(this)
        );
    }

    /** @hidden */
    private onPlayResult = (resultJSON: string): void =>
    {
        let playState: IPlayState = JSON.parse(resultJSON);

        // When first starting up, result data is stored in play state, if there pull it out.
        // tslint:disable-next-line:no-string-literal
        let playStateJSON: string = playState["playState"];
        if (playStateJSON)
        {
            // Stored as json string property
            playState = JSON.parse(playStateJSON);
        }

        if (playState != null)
        {
            let betConfig: IBetConfig = this._gameSetupRef.Value.GetBetConfig(parseInt(playState.betConfigId, 10));
            let currentBet: number = betConfig.betInCredits * playState.platformBetMult;
            this._betManagerRef.Value.CurrentBetCredits = currentBet;
            this._betManagerRef.Value.ActiveDenom = playState.denomMc;
            this._betManagerRef.Value.CurrentBetConfig = betConfig;
            this._betManagerRef.Value.SetBalance(playState.startRoundMc);
            CLog.log(`Setting bet to [${currentBet}] credits`, LogTypes.All);
            this._betManagerRef.Value.PlaceBet();
        }
        else
        {
            CLog.error("Malformed requestPlayResult response.", this, LogTypes.All);
        }

        this.completeStateOptionalNext(this._checkRecallOnState.Value);
    }

    /**
     * Called when updateRecallDataState is reached.
     * Retrieves an updated recall history from the RGS server.
     */
    public PopulateRecallData(): void
    {
        this._communicatorRef.WithValue(communicator =>
            {
                communicator.RequestRecallSummary(0, this._gameSetupRef.Value.RecallHistory, this.onGetRecallData);
            });
    }

    private _cachedRecallData: string = null;

    /**
     * Handler for when recall data is retrieved from the RGS server.
     * @param result The raw JSON string of recall data.
     * @hidden
     */
    private onGetRecallData = (result: string): void =>
    {
        if (!result || this._cachedRecallData === result) { return; }

        let objData: any = JSON.parse(result);

        this._recallEntries.length = 0;

        for (let recallData of objData)
        {
            if ((recallData as IRecallSummary).gameOver && !(recallData as IRecallSummary).gameCanceled)
            {
                this.addRecallEntry(recallData);
            }
        }

        this.refreshRecallUi();
        this._cachedRecallData = result;
    }

    private refreshRecallUi(): void
    {
        this._popupManagerRef.WithValue(popupManager =>
            {
                popupManager.ClearRecallEntries();
                if (!this._recallEntries || this._recallEntries.length < 1) { return; }
                popupManager.UpdateRecallEntries(this._recallEntries);
            });
    }

    /**
     * Helper function for constructing [[RecallEntry]] UI elements and adding them to the layout.
     * @param recallData The recall data for the entry to be added.
     * @hidden
     */
    private addRecallEntry(recallData: IRecallSummary): void
    {
        let betMc: number = recallData.betMc;
        let betCurrency: Currency = new Currency(recallData.denomMc, betMc, CurrencyType.MC);
        let winCurrency: Currency = new Currency(recallData.denomMc, recallData.winMc, CurrencyType.MC);
        let balanceCurrency: Currency = new Currency(recallData.denomMc, recallData.endBalanceMc, CurrencyType.MC);
        let time: Date = new Date(0);
        let currencyType: CurrencyType
            = this._gameSetupRef.Value.WagerDisplay === WagerDisplay.Credits
                ? CurrencyType.CREDITS
                : CurrencyType.CASH;
        time.setUTCMilliseconds(parseInt(recallData.timeStamp, 10));

        const dateTimeDisplay: string = this._localizationComponent.Value.GetTranslatedDateTimeDisplay(parseInt(recallData.timeStamp, 10), new DateTimeConfiguration());

        const recallEntry: RecallEntryData = new RecallEntryData(
            this._recallEntries.length + 1,
            dateTimeDisplay,
            betCurrency.GetValue(currencyType),
            winCurrency.GetValue(currencyType),
            balanceCurrency.GetValue(currencyType),
            currencyType,
            (): void => { this.InitiateRecall(recallData.roundId); }
        );

        this._recallEntries.push(recallEntry);
    }

    /**
     * Helper method for opening a new tab to initiate a recall play.
     * @param recallIndex The play ID to be recalled.
     * @hidden
     */
    public InitiateRecall = (recallIndex: number): void =>
    {
        
        const gameSetup: GameSetup = this._gameSetupRef.Value;
        if (!gameSetup) { return; }

        const protocol: string = document.location.protocol;
        const host: string = window.location.host;

        cc.sys.openURL(`${protocol}//${host}/gateway/gamehost/${gameSetup.GetGameUri({ recall: recallIndex })}`);
    }

    /**
     * Enables the Recall UI cover.
     */
    public StartRecallMode(): void
    {
        if (this.coverNodeReference.Value)
        {
            this.coverNodeReference.Value.node.active = true;
        }

        let playBar: IPlaybar = this._playbarRef.Value;
        if (playBar)
        {
            playBar.ToggleOff(true);
        }
        else
        {
            CLog.warn("Could not get playbar to turn off", LogTypes.All);
        }
    }

    /**
     * Disables the Recall UI cover.
     */
    public EndRecallMode(): void
    {
        if (this.coverNodeReference.Value)
        {
            this.coverNodeReference.Value.node.active = false;
        }
    }
}
